/**
 * Merge filtered DnD results into full order; coalesce by groupName for persistence
 * For Media Devoted | Created By Ciro Autiero
 */

import type { Preset } from '../../shared';

/** Replace filtered positions in full order with `newFilteredOrder` (same multiset). */
export function mergeFilteredReorder(
  fullOrderedIds: string[],
  newFilteredOrder: string[],
  filteredIdSet: Set<string>,
): string[] {
  let j = 0;
  const out = fullOrderedIds.map((id) => {
    if (!filteredIdSet.has(id)) return id;
    const next = newFilteredOrder[j];
    j += 1;
    if (next === undefined) {
      throw new Error('mergeFilteredReorder: ran out of new order ids');
    }
    return next;
  });
  if (j !== newFilteredOrder.length) {
    throw new Error('mergeFilteredReorder: newFilteredOrder length does not match filtered slots');
  }
  return out;
}

/** One contiguous block per groupKey; group order = first appearance in orderedIds. */
export function coalescePresetOrderByGroup(
  orderedIds: string[],
  presets: Pick<Preset, 'id' | 'groupName'>[],
): string[] {
  const idToGroup = new Map(presets.map((p) => [p.id, p.groupName?.trim() ?? ''] as const));
  const groupOrder: string[] = [];
  const seen = new Set<string>();
  for (const id of orderedIds) {
    const g = idToGroup.get(id);
    if (g === undefined) continue;
    if (!seen.has(g)) {
      seen.add(g);
      groupOrder.push(g);
    }
  }
  const buckets = new Map<string, string[]>();
  for (const g of groupOrder) {
    buckets.set(g, []);
  }
  for (const id of orderedIds) {
    const g = idToGroup.get(id);
    if (g === undefined) continue;
    buckets.get(g)?.push(id);
  }
  const out: string[] = [];
  for (const g of groupOrder) {
    out.push(...(buckets.get(g) ?? []));
  }
  return out;
}
